import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { SyndicateTypeEnumType } from '__generated__/graphql';
import { FC, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import CoSyndicateApplicableUserSlideOver, {
  CoSyndicateApplicableUser,
} from 'routes/SyndicateDashboard/SyndicateNetwork/Invites/CoSyndicateApplicableUserSlideOver';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import FilterPanel from 'components/FilterPanel';
import { FormInput, FormPanel } from 'components/FormPanel';
import EditorFormInput from 'components/QuillEditor/EditorFormInput';

import Alert from 'primitives/Alert';
import Badge from 'primitives/Badge';
import LoadingIndicator from 'primitives/LoadingIndicator';
import TextField from 'primitives/TextField';
import Toast from 'primitives/Toast';
import useToast from 'primitives/Toast/useToast';

import debounce from 'utils/debounce';
import { getDeterministicColor } from 'utils/random-colors';

import InviteUsersEmailPreviewDialogButton from './InviteUsersEmailPreviewDialogButton';

const DEAL_INFORMATION_INVITE_QUERY = gql(`
  query DealInformationForInvite($id: ID!, $cursor: ID, $filters: SyndicateUsersFilterType, $coSyndicateFilters: CoSyndicateApplicableUsersFilterType) {
    deal(id: $id) {
      id
      permissions {
        canInviteUsers
      }
      syndicate {
        id
        type
        syndicateUsers(cursor: $cursor, filters: $filters) {
          nodes {
            id
            user {
              id
              name
              email
            }
            createdAt
            statistics {
              investRate
            }
          }
          pageInfo {
            hasNextPage
            cursor
            totalCount
          }
        }
        coSyndicateApplicableUsers(filters: $coSyndicateFilters) {
          user {
            id
            name
            image {
              url
              name
            }
            email
            linkedInProfileUrl
            createdAt
            statistics {
              numberOfSyndicatesJoined
              totalInvestedAmount
              numberOfInvestments
              investRate
            }
          }
          syndicateUsers {
            id
            createdAt
            statistics {
              totalInvestedAmount
              numberOfInvestments
              investRate
            }
            syndicate {
              id
              name
              image
            }
          }
        }
      }
    }
  }
`);

const INVITE_USERS_TO_DEAL_MUTATION = gql(`
  mutation InviteUsersToDeal(
    $dealId: ID!
    $userIds: [ID]!
    $note: String
    $minimumInvestmentAmount: Int
    $maximumInvestmentAmount: Int
  ) {
    inviteUsersToDeal(
      id: $dealId
      userIds: $userIds
      note: $note
      minimumInvestmentAmount: $minimumInvestmentAmount
      maximumInvestmentAmount: $maximumInvestmentAmount
    )
  }
`);

const InviteNetwork: FC = () => {
  const { dealId } = useParams<{ syndicateId: string; dealId: string }>() as {
    syndicateId: string;
    dealId: string;
  };

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedCoSyndicateUserIds, setSelectedCoSyndicateUserIds] = useState<string[]>([]);
  const [coSyndicateApplicableUserOnDisplay, setCoSyndicateApplicableUserOnDisplay] =
    useState<CoSyndicateApplicableUser | null>(null);
  const [openEmailPreview, setOpenEmailPreview] = useState(false);
  const [formData, setFormData] = useState<any>(null);
  const { showToast, handleToastOpen, handleToastClose } = useToast();

  const { loading, error, data, refetch, variables, fetchMore, networkStatus } = useQuery(
    DEAL_INFORMATION_INVITE_QUERY,
    {
      variables: {
        id: dealId,
        filters: {
          isEligibleForDealId: dealId,
          text: '',
        },
        coSyndicateFilters: {
          syndicateId: undefined,
          text: undefined,
        },
      },
    }
  );

  const [inviteUsersToDeal, { loading: inviteUsersToDealLoading, error: inviteUsersToDealError }] =
    useMutation(INVITE_USERS_TO_DEAL_MUTATION, {
      refetchQueries: ['GetActiveInvites', 'DealInformationForInvite'],
    });

  const coSyndicateApplicableUsers = useMemo(
    () => data?.deal?.syndicate?.coSyndicateApplicableUsers || [],
    [data]
  );

  const uniqueCoSyndicatesWithColors = useMemo(() => {
    if (!coSyndicateApplicableUsers) return [];
    const allSyndicates = coSyndicateApplicableUsers.flatMap(
      coSyndicate =>
        coSyndicate?.syndicateUsers?.map(syndicateUser => syndicateUser.syndicate) || []
    );
    const uniqueSyndicatesMap = new Map();
    allSyndicates.forEach((syndicate, index) => {
      if (syndicate && syndicate.id && !uniqueSyndicatesMap.has(syndicate.id)) {
        uniqueSyndicatesMap.set(syndicate.id, {
          ...syndicate,
          color: getDeterministicColor(index.toString()),
        });
      }
    });
    return Array.from(uniqueSyndicatesMap.values());
  }, [coSyndicateApplicableUsers]);

  if (loading) return <LoadingIndicator />;
  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const { nodes: syndicateUsers, pageInfo } = data.deal.syndicate.syndicateUsers;
  const isRuvSyndicate = data.deal.syndicate.type === SyndicateTypeEnumType.Ruv;
  const isCoSyndicate = data.deal.syndicate.type === SyndicateTypeEnumType.CoSyndicate;
  const canInviteUsers = data.deal.permissions.canInviteUsers;

  const onSubmitAfterEmailPreview = async () => {
    inviteUsersToDeal({
      variables: {
        dealId,
        userIds: [...selectedUserIds, ...selectedCoSyndicateUserIds],
        note: formData.note,
        minimumInvestmentAmount: formData.minimumInvestmentAmount,
        maximumInvestmentAmount: formData.maximumInvestmentAmount,
      },
    });
  };

  const filters = [
    {
      type: 'syndicateId',
      label: 'Syndicate',
      options: [
        {
          label: 'All',
          value: undefined,
        },
        ...uniqueCoSyndicatesWithColors.map(syndicate => ({
          label: syndicate.name,
          value: syndicate.id,
        })),
      ],
    },
  ];

  return (
    <>
      <CoSyndicateApplicableUserSlideOver
        open={!!coSyndicateApplicableUserOnDisplay}
        coSyndicateApplicableUser={coSyndicateApplicableUserOnDisplay}
        onClose={() => setCoSyndicateApplicableUserOnDisplay(null)}
      />
      {!canInviteUsers && (
        <Alert
          type="warning"
          title="Invites Disabled"
          message="You can only invite users to a deal that is in the raising state."
        />
      )}

      {!isRuvSyndicate && (
        <>
          <div className="sm:flex sm:items-center sm:justify-between">
            <div className="max-w-xl mb-3">
              <h3 className="text-lg font-semibold leading-6 text-gray-700">Invite your network</h3>
              <p className="mt-1 text-md text-gray-500">
                Send out invites to your network to join this deal.
              </p>
            </div>
          </div>
          <div className="flex justify-between items-end mb-5">
            <TextField
              placeholder="Search for an investor"
              onChange={debounce(e => {
                refetch({
                  filters: {
                    ...variables?.filters,
                    text: e.target.value,
                  },
                });
              }, 300)}
            />
            <span className="text-sm text-gray-500">
              {syndicateUsers.length} of {pageInfo.totalCount} users
            </span>
          </div>
          <DataTable
            data={syndicateUsers}
            columns={[
              {
                label: 'Select',
                fieldName: 'user.id',
                type: 'SELECT',
              },
              {
                label: 'Name',
                fieldName: 'user.name',
              },
              {
                label: 'Email',
                fieldName: 'user.email',
                type: 'COPY_STRING',
              },
              {
                label: 'Joined Syndicate',
                fieldName: 'createdAt',
                type: 'DATE',
              },
              {
                label: 'Invest Rate In Your Syndicate',
                fieldName: 'statistics.investRate',
                type: 'PERCENTAGE',
              },
            ]}
            onSelect={(userIds: string[]) => {
              setSelectedCoSyndicateUserIds([]);
              setSelectedUserIds(userIds);
            }}
            hasNextPage={pageInfo.hasNextPage}
            filterLoading={networkStatus === NetworkStatus.setVariables}
            onLoadMore={() =>
              fetchMore({
                variables: {
                  cursor: pageInfo.cursor,
                },
              })
            }
          />
        </>
      )}

      {selectedUserIds && selectedUserIds.length && canInviteUsers ? (
        <FormPanel
          loading={inviteUsersToDealLoading}
          error={inviteUsersToDealError}
          onSubmit={data => {
            setFormData(data);
            setOpenEmailPreview(true);
          }}
          submitButtonLabel="Preview Invitation"
        >
          <FormInput
            fullWidth
            type="custom"
            fieldName="note"
            label="Note"
            defaultValue={''}
            customInput={EditorFormInput}
          />
          <FormInput
            type="currency"
            fieldName="minimumInvestmentAmount"
            label="Minimum Investment Amount"
            defaultValue={300000}
          />
          <FormInput
            type="currency"
            fieldName="maximumInvestmentAmount"
            label="Maximum Investment Amount"
            defaultValue={1000000}
          />
        </FormPanel>
      ) : null}

      {isCoSyndicate && coSyndicateApplicableUsers && (
        <>
          <div className="mt-8 sm:flex sm:items-center sm:justify-between">
            <div className="max-w-xl">
              <h3 className="text-lg font-semibold leading-6 text-gray-700">
                Find previous investors
              </h3>
              <p className="mt-1 text-md text-gray-500">
                Find previous investors who have invested in your syndicate.
              </p>
            </div>
          </div>
          <FilterPanel
            activeFilters={variables?.coSyndicateFilters}
            filters={filters}
            refetch={refetch}
            filterKey="coSyndicateFilters"
          />
          <TextField
            placeholder="Search for an investor"
            onChange={debounce(e => {
              refetch({
                coSyndicateFilters: {
                  ...variables?.coSyndicateFilters,
                  text: e.target.value,
                },
              });
            }, 500)}
          />
          <DataTable
            data={coSyndicateApplicableUsers}
            onClick={user => setCoSyndicateApplicableUserOnDisplay(user)}
            columns={[
              {
                label: 'Select',
                fieldName: 'user.id',
                type: 'SELECT',
              },
              {
                label: 'Name',
                fieldName: 'user.name',
              },
              {
                label: 'Email',
                fieldName: 'user.email',
                type: 'COPY_STRING',
              },
              {
                label: 'Associations',
                fieldName: 'syndicateUsers',
                type: 'CUSTOM',
                customValue: (value: any) => {
                  return (
                    <div className="flex flex-col gap-2">
                      {value.map((syndicateUser: any) => (
                        <Badge
                          key={syndicateUser.syndicate.id}
                          label={syndicateUser.syndicate.name}
                          color={
                            uniqueCoSyndicatesWithColors?.find(
                              s => s?.id === syndicateUser.syndicate.id
                            )?.color
                          }
                        />
                      ))}
                    </div>
                  );
                },
              },
            ]}
            onSelect={(userIds: string[]) => {
              setSelectedUserIds([]);
              setSelectedCoSyndicateUserIds(userIds);
            }}
          />
        </>
      )}

      {selectedCoSyndicateUserIds && selectedCoSyndicateUserIds.length && canInviteUsers ? (
        <FormPanel
          loading={inviteUsersToDealLoading}
          error={inviteUsersToDealError}
          onSubmit={data => {
            setFormData(data);
            setOpenEmailPreview(true);
          }}
          submitButtonLabel="Preview Invitation"
        >
          <FormInput type="textarea" fieldName="note" label="Note" defaultValue={''} />
          <FormInput
            type="currency"
            fieldName="minimumInvestmentAmount"
            label="Minimum Investment Amount"
            defaultValue={300000}
          />
          <FormInput
            type="currency"
            fieldName="maximumInvestmentAmount"
            label="Maximum Investment Amount"
            defaultValue={1000000}
          />
        </FormPanel>
      ) : null}

      <div className="mb-10">
        <InviteUsersEmailPreviewDialogButton
          open={openEmailPreview}
          setOpen={setOpenEmailPreview}
          handleToastOpen={handleToastOpen}
          onSubmit={onSubmitAfterEmailPreview}
          formData={formData}
          dealId={dealId}
        />
      </div>

      <Toast
        title="Success"
        message={'Invites sent successfully'}
        show={showToast}
        handleClose={handleToastClose}
      />
    </>
  );
};

export default InviteNetwork;
